import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import { Duration } from "luxon";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

/** Where a scanned QR code landed, as inferred from the browser environment. */
export type QrLoginPlatform = "android" | "ios" | "unknown";

/** Play Store listing; `playStoreUrl` appends the install referrer that carries the token through installation. */
const PLAY_STORE_LISTING = "https://play.google.com/store/apps/details?id=com.storyboardpodcasts.walkietalkie";

/** App Store listing. iOS has no install-referrer equivalent, so the token never rides along. */
export const APP_STORE_URL = "https://apps.apple.com/app/id1662743814";

/** Interstitial dwell before forwarding to the store, long enough to read it and reach "Already installed?". */
const REDIRECT_DELAY = Duration.fromObject({ seconds: 5 });

/**
 * Classifies the scanning device from its user agent.
 *
 * `unknown` is not an error state: it is the desktop case and the misdetection case, and it renders the
 * badge page instead of redirecting.
 *
 * @param maxTouchPoints distinguishes iPadOS, whose Safari reports a Macintosh user agent; a touch-capable
 *   "Macintosh" is an iPad.
 */
export function detectQrLoginPlatform(userAgent: string, maxTouchPoints = 0): QrLoginPlatform {
  if (/android/i.test(userAgent)) {
    return "android";
  }
  if (/iPad|iPhone|iPod/.test(userAgent) || (/Macintosh/.test(userAgent) && maxTouchPoints > 1)) {
    return "ios";
  }
  return "unknown";
}

/**
 * Play Store URL with the QR token attached as an install referrer, so first launch after installation
 * can sign the driver in with no further action. Without a token, the bare listing.
 */
export function playStoreUrl(token: string | null): string {
  if (!token) {
    return PLAY_STORE_LISTING;
  }
  return `${PLAY_STORE_LISTING}&referrer=${encodeURIComponent(`qr_token=${token}`)}`;
}

/** Deep link for users who already have the app but whose scan landed in the browser anyway. */
export function qrLoginDeepLink(token: string): string {
  return `storyboard://qr-login?token=${encodeURIComponent(token)}`;
}

/**
 * Schedules the interstitial's forward to the store, returning the cancel function for effect cleanup.
 *
 * No-op without a token or on unknown platforms, which render the badge page instead of redirecting.
 */
export function scheduleStoreRedirect(
  token: string | null,
  platform: QrLoginPlatform,
  redirect: (url: string) => void,
): (() => void) | undefined {
  if (!token || platform === "unknown") {
    return undefined;
  }
  const storeUrl = platform === "android" ? playStoreUrl(token) : APP_STORE_URL;
  const timeout = setTimeout(() => redirect(storeUrl), REDIRECT_DELAY.toMillis());
  return () => clearTimeout(timeout);
}

const pageSx = {
  width: "100%",
  height: "100%",
  position: "fixed",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "#222",
  color: "#fff",
} as const;

const cardSx = {
  padding: 3,
  borderRadius: 2,
  background: "#333",
  textAlign: "center",
  maxWidth: 420,
} as const;

function StoreBadges({ token }: { token: string | null }) {
  return (
    <Box sx={{ display: "flex", gap: 2, justifyContent: "center", mb: 2 }}>
      <Button variant="contained" href={playStoreUrl(token)}>
        Get it on Google Play
      </Button>
      <Button variant="contained" href={APP_STORE_URL}>
        Download on the App Store
      </Button>
    </Box>
  );
}

function AlreadyInstalled({ token }: { token: string }) {
  return (
    <Button variant="outlined" href={qrLoginDeepLink(token)}>
      Already installed? Open the app
    </Button>
  );
}

/**
 * Presentational body of the QR landing page, split from the route component so every platform variant
 * can be rendered directly in tests.
 *
 * This page must never call the redemption endpoint: redemption is one-time and irreversible, so
 * redeeming here would burn the credential before the app exists to use it. It must also never write
 * the token to logs or analytics.
 */
export function QrLoginView({ platform, token }: { platform: QrLoginPlatform; token: string | null }) {
  if (!token) {
    return (
      <Box sx={pageSx}>
        <Box sx={cardSx}>
          <Box component="h1" sx={{ fontSize: "1.5rem", m: 0, mb: 1, fontWeight: "bold" }}>
            Sign in with QR code
          </Box>
          <Box sx={{ mb: 2 }}>
            This link is missing its sign-in code. Get the Storyboard app, then scan the QR code on your work device
            again.
          </Box>
          <StoreBadges token={null} />
        </Box>
      </Box>
    );
  }

  if (platform === "unknown") {
    return (
      <Box sx={pageSx}>
        <Box sx={cardSx}>
          <Box component="h1" sx={{ fontSize: "1.5rem", m: 0, mb: 1, fontWeight: "bold" }}>
            Sign in with QR code
          </Box>
          <Box sx={{ mb: 2 }}>Get the Storyboard app for your phone, then scan the QR code again to sign in.</Box>
          <StoreBadges token={token} />
          <AlreadyInstalled token={token} />
        </Box>
      </Box>
    );
  }

  const storeName = platform === "android" ? "Google Play" : "the App Store";
  const storeUrl = platform === "android" ? playStoreUrl(token) : APP_STORE_URL;

  return (
    <Box sx={pageSx}>
      <Box sx={cardSx}>
        <Box component="h1" sx={{ fontSize: "1.5rem", m: 0, mb: 1, fontWeight: "bold" }}>
          Taking you to {storeName}&hellip;
        </Box>
        {platform === "ios" ? (
          <Box sx={{ mb: 2 }}>
            After installing, open the app and choose &ldquo;Sign in with QR code&rdquo; on the login screen, then scan
            the code on your work device again.
          </Box>
        ) : (
          <Box sx={{ mb: 2 }}>Install the app and it will sign you in when it first opens.</Box>
        )}
        <Box sx={{ mb: 2 }}>
          <Button variant="contained" href={storeUrl}>
            Open {storeName}
          </Button>
        </Box>
        <AlreadyInstalled token={token} />
      </Box>
    </Box>
  );
}

/**
 * Landing page a scanned QR code falls back to when the app is not installed to intercept the link.
 *
 * Forwards Android and iOS to their store listings after a brief interstitial; desktop and undetected
 * platforms see both store badges instead.
 */
export default function QrLogin() {
  const [searchParams] = useSearchParams();
  // Captured once: the scrub below rewrites the URL, and the token must survive it.
  const [token] = useState(() => searchParams.get("token"));
  const platform = detectQrLoginPlatform(navigator.userAgent, navigator.maxTouchPoints ?? 0);

  // Scrub the one-time token from the address bar as soon as it is read, so nothing that samples the
  // URL later — error reporting, a copied link — can see it.
  useEffect(() => {
    if (!token) {
      return;
    }
    const url = new URL(window.location.href);
    url.searchParams.delete("token");
    window.history.replaceState(null, "", url);
  }, [token]);

  useEffect(() => {
    // location.replace drops the interstitial from history, so pressing back from the store cannot land
    // here again and re-fire the redirect into a loop.
    return scheduleStoreRedirect(token, platform, (url) => window.location.replace(url));
  }, [token, platform]);

  return <QrLoginView platform={platform} token={token} />;
}
